/**
 * Single source of truth for every Plaid Link telemetry signal: Amplitude
 * dispatches and Sentry captures/breadcrumbs. Both the fresh-link page, the
 * OAuth-resume page, and the shared submit hook emit through here so the funnel
 * stays consistent and we never drift between the two pages.
 *
 * PII rule: this module adds NO email/name to its Sentry tags or extra, and
 * sanitizes Plaid metadata (institution/account names, masks) before it reaches
 * Sentry/console. The logged-in user's email is still attached to every event
 * globally via Sentry.setUser in AuthProvider, so failures remain attributable;
 * account id + link_session_id (+ request_id when Plaid provides it) correlate
 * a failure with backend/Plaid logs.
 */
import * as Sentry from "@sentry/react";
import type {
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
} from "react-plaid-link";

import AmplitudeProvider from "~/AmplitudeProvider";

export type PlaidFlowMetadata =
  | PlaidLinkOnSuccessMetadata
  | PlaidLinkOnExitMetadata;

export type ResumeUnavailableReason =
  | "no_session"
  | "expired_or_invalid"
  | "no_oauth_state_id";

/**
 * Base Sentry tags shared by every Plaid capture. No PII: only flow/step/account
 * /product. `link_session_id` / `request_id` are layered on per-event when known.
 */
const baseSentryTags = (accountID: string) =>
  ({
    flow: "funding",
    step: "plaid",
    currentAccountId: accountID,
    product: "live",
  }) as const;

interface CapturePlaidErrorOptions {
  error?: unknown;
  metadata?: PlaidFlowMetadata;
  linkSessionId?: string;
}

/** PII-free projection of Plaid metadata safe to ship to Sentry/console. */
export interface SanitizedPlaidMetadata {
  link_session_id?: string;
  request_id?: string;
  /** Institution id ONLY — the human-readable institution name is dropped. */
  institution_id?: string;
  accounts_count?: number;
  /** Account shape WITHOUT `name`/`mask` (both financial PII). */
  accounts?: Array<{
    id?: string;
    type?: string | null;
    subtype?: string | null;
    verification_status?: string | null;
  }>;
  error_code?: string;
  error_type?: string;
  status?: string;
}

/**
 * Strip financial PII from Plaid Link metadata before it leaves the client.
 * Plaid metadata embeds `institution.name`, `accounts[].name`, and
 * `accounts[].mask` — all PII that must NEVER reach Sentry/console. This returns
 * ONLY the non-PII fields useful for correlating a failure with backend/Plaid
 * logs (ids, counts, codes, verification status).
 */
export const sanitizePlaidMetadata = (
  metadata?: PlaidFlowMetadata,
): SanitizedPlaidMetadata | undefined => {
  if (!metadata) {
    return undefined;
  }

  const sanitized: SanitizedPlaidMetadata = {
    link_session_id:
      typeof metadata.link_session_id === "string"
        ? metadata.link_session_id
        : undefined,
  };

  if ("request_id" in metadata && typeof metadata.request_id === "string") {
    sanitized.request_id = metadata.request_id;
  }

  if ("status" in metadata && typeof metadata.status === "string") {
    sanitized.status = metadata.status;
  }

  if ("error_code" in metadata && typeof metadata.error_code === "string") {
    sanitized.error_code = metadata.error_code;
  }

  if ("error_type" in metadata && typeof metadata.error_type === "string") {
    sanitized.error_type = metadata.error_type;
  }

  const institution =
    "institution" in metadata ? metadata.institution : undefined;
  if (
    institution &&
    typeof institution === "object" &&
    "institution_id" in institution &&
    typeof institution.institution_id === "string"
  ) {
    sanitized.institution_id = institution.institution_id;
  }

  if ("accounts" in metadata && Array.isArray(metadata.accounts)) {
    sanitized.accounts_count = metadata.accounts.length;
    // DROP `name` and `mask`; keep only non-PII identifiers/status.
    sanitized.accounts = metadata.accounts.map((account) => ({
      id: account.id,
      type: account.type ?? null,
      subtype: account.subtype ?? null,
      verification_status: account.verification_status ?? null,
    }));
  }

  return sanitized;
};

/** PII-free projection of an arbitrary throwable, safe to ship to Sentry. */
export interface SafePlaidError {
  name: string;
  message: string;
  /** e.g. axios `ECONNABORTED`, `ERR_BAD_REQUEST`. */
  code?: string;
  /** HTTP status only — NEVER the response body. */
  http_status?: number;
  http_status_text?: string;
}

/**
 * Project an arbitrary throwable down to PII-free fields. The money-path callers
 * pass an AxiosError whose `config.data` (the relationship request body) and
 * `response.data` carry `public_token`, account `name`, and `mask`. We keep ONLY
 * name/message/code + HTTP status and explicitly drop `config`, `request`,
 * `response`, and headers so none of that reaches Sentry.
 */
export const toSafeError = (error: unknown): SafePlaidError | undefined => {
  if (error == null) {
    return undefined;
  }
  if (typeof error !== "object") {
    return { name: "NonError", message: String(error) };
  }
  const raw = error as Record<string, unknown>;
  const response = raw.response as
    | { status?: unknown; statusText?: unknown }
    | undefined;
  return {
    name: typeof raw.name === "string" ? raw.name : "Error",
    message: typeof raw.message === "string" ? raw.message : "Unknown error",
    code: typeof raw.code === "string" ? raw.code : undefined,
    http_status:
      typeof response?.status === "number" ? response.status : undefined,
    http_status_text:
      typeof response?.statusText === "string"
        ? response.statusText
        : undefined,
  };
};

/**
 * The ONLY way Plaid errors reach Sentry. PII rule: the captured exception and
 * `extra` are both built from a sanitized projection — we NEVER hand Sentry the
 * raw throwable, because `extraErrorDataIntegration` (see Sentry.ts) would
 * harvest an AxiosError's `config.data`/`response.data` (public_token, name,
 * mask) into the event. `request_id` is pulled off exit metadata when present
 * for cross-log correlation; metadata is sanitized before it reaches `extra`.
 */
export const capturePlaidError = (
  accountID: string,
  message: string,
  opts: CapturePlaidErrorOptions = {},
): void => {
  const sanitizedMetadata = sanitizePlaidMetadata(opts.metadata);
  const safeError = toSafeError(opts.error);

  if (window.env.ENV !== "PROD") {
    console.error(`Plaid Error: ${message}`, {
      error: opts.error,
      metadata: sanitizedMetadata,
    });
  }

  // Build a plain Error from sanitized fields so the integration finds no
  // non-standard props to harvest. Preserve the original stack (PII-free) for
  // debugging when the throwable is an Error.
  const sentryError = new Error(
    safeError
      ? `${message} (${safeError.name}: ${safeError.message})`
      : message,
  );
  if (opts.error instanceof Error && opts.error.stack) {
    sentryError.stack = opts.error.stack;
  }

  const requestId =
    opts.metadata && "request_id" in opts.metadata
      ? opts.metadata.request_id
      : undefined;

  Sentry.captureException(sentryError, {
    level: "error",
    tags: {
      ...baseSentryTags(accountID),
      link_session_id: opts.linkSessionId,
      request_id: requestId,
    },
    extra: {
      error: safeError,
      metadata: sanitizedMetadata,
      message,
    },
  });
};

/**
 * Breadcrumb for an in-Link ERROR event. We intentionally do NOT capture or
 * toast here: ERROR also fires for recoverable in-flow errors (e.g. bad bank
 * credentials the user retries). User-facing errors are surfaced once in onExit.
 */
export const addPlaidErrorBreadcrumb = (
  metadata: PlaidLinkOnEventMetadata,
): void => {
  Sentry.addBreadcrumb({
    category: "plaid",
    level: "warning",
    message: `Plaid ERROR event: ${metadata.error_code ?? "unknown"}`,
    data: {
      error_code: metadata.error_code,
      error_type: metadata.error_type,
      link_session_id: metadata.link_session_id,
    },
  });
};

// --- Amplitude dispatches -------------------------------------------------
// `plaid_event` carries a JSON string payload (matches the legacy schema so
// existing dashboards keep working); the rest dispatch structured objects.

/** Generic Plaid lifecycle event (OPEN/HANDOFF/EXIT/ERROR/RELATIONSHIP_*, etc). */
export const trackPlaidEvent = (data: Record<string, unknown>): void => {
  AmplitudeProvider.dispatch("plaid_event", JSON.stringify(data));
};

/** Terminal success signal once the relationship is created/verified. */
export const trackPlaidFinished = (data: Record<string, unknown>): void => {
  AmplitudeProvider.dispatch("plaid_finished", JSON.stringify(data));
};

export const trackOAuthSessionSaveResult = (saved: boolean): void => {
  AmplitudeProvider.dispatch(
    saved ? "plaid_oauth_session_saved" : "plaid_oauth_session_save_failed",
    { is_oauth_capable: saved },
  );
};

/**
 * Entry signal for the whole resume funnel. `age_ms` = time spent in the bank's
 * OAuth flow before returning; high values explain expired-token failures on
 * slow mobile redirects.
 */
export const trackResumeDetected = (args: {
  age_ms: number;
  had_search: boolean;
}): void => {
  AmplitudeProvider.dispatch("plaid_oauth_resume_detected", args);
};

/**
 * Completes the resume funnel: detected → Link ready → auto-opened. A gap
 * between resume_detected and this flags an init failure (e.g.
 * receivedRedirectUri mismatch) rather than a user drop-off.
 */
export const trackResumeAutoOpened = (): void => {
  AmplitudeProvider.dispatch("plaid_oauth_resume_auto_opened", {});
};

/**
 * We landed on the OAuth-return page but could not resume.
 *  - "no_session": returned with oauth_state_id but nothing was persisted
 *    (storage blocked at save time, or the user returned on a different
 *    device/account).
 *  - "expired_or_invalid": returned with oauth_state_id but the persisted
 *    session failed validation (TTL, expiration, account mismatch) by the time
 *    the redirect returned.
 *  - "no_oauth_state_id": not a real OAuth return (e.g. direct navigation to the
 *    page). Distinguished so dashboards don't read errant visits as genuine
 *    token expirations, and so a still-valid persisted session is left intact.
 */
export const trackResumeUnavailable = (args: {
  reason: ResumeUnavailableReason;
  oauth_state_id_present: boolean;
}): void => {
  AmplitudeProvider.dispatch("plaid_oauth_resume_unavailable", args);
};

export const trackResumeTokenExpired = (args: {
  link_session_id?: string;
}): void => {
  AmplitudeProvider.dispatch("plaid_oauth_resume_token_expired", args);
};

export const trackLinkValidationFailed = (args: {
  reason: string;
  link_session_id?: string;
}): void => {
  AmplitudeProvider.dispatch("plaid_link_validation_failed", args);
};

export const trackAuthStatusFailed = (args: {
  plaid_account_id: string;
}): void => {
  AmplitudeProvider.dispatch("plaid_auth_status_failed", args);
};

export const trackRelationshipFailed = (args: {
  error_message: string;
  verification_status: string;
}): void => {
  AmplitudeProvider.dispatch("plaid_relationship_failed", args);
};

/**
 * A duplicate onSuccess was swallowed by the idempotency latch before it could
 * reach a second POST /relationships. A spike here means Plaid/SDK is replaying
 * onSuccess and the latch is doing its job (PRIME-698).
 */
export const trackDuplicateSuccessSuppressed = (args: {
  link_session_id?: string;
}): void => {
  AmplitudeProvider.dispatch("plaid_duplicate_success_suppressed", args);
};
